/* 
  Table component elements built in accordance with our new Table design specification:

  https://www.figma.com/design/vSpyLjZQbEn9KCClyX5ytF/Information-Architecture?node-id=3762-10650&t=p81yOYj25fQIDqSF-4
*/

import classNames from 'classnames';
import { type AllHTMLAttributes, useMemo } from 'react';

import { Box } from '../Box/Box';
import { Skeleton } from '../Skeleton/Skeleton';
import * as styles from './styles.css';

const Row = () => (
  <Box
    className={styles.skeletonRowStyle}
    width="full"
    display="flex"
    justifyContent="center"
    alignItems="center"
    padding="medium"
  >
    <Skeleton height="24px" width="100%" />
  </Box>
);

export const TableSkeleton = ({ rowCount = 4 }: { rowCount?: number }) => {
  const rows = useMemo(() => {
    const _rows = [];
    let i = 0;
    while (i < rowCount) {
      _rows.push(<Row key={`tsr-${i}`} />);
      i++;
    }
    return _rows;
  }, [rowCount]);

  return (
    <Box width="full">
      <Box
        width="full"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="medium"
        className={styles.skeletonHeaderRowStyle}
      />
      {rows}
    </Box>
  );
};

export const Table = ({
  ref,
  children,
  className,
  ...restProps
}: AllHTMLAttributes<HTMLTableElement> & {
  ref?: React.Ref<HTMLTableElement>;
}) => (
  <table ref={ref} {...restProps} className={classNames(styles.tableStyle, className)}>
    {children}
  </table>
);
Table.displayName = 'Table';

export const TableHead = ({
  ref,
  children,
  className,
  sticky = true,
  ...restProps
}: AllHTMLAttributes<HTMLTableSectionElement> & {
  sticky?: boolean;
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <thead ref={ref} {...restProps} className={classNames(styles.tableHeadStyle({ sticky }), className)}>
    {children}
  </thead>
);
TableHead.displayName = 'TableHead';

export const TableHeadRow = ({
  ref,
  children,
  ...restProps
}: AllHTMLAttributes<HTMLTableRowElement> & {
  ref?: React.Ref<HTMLTableRowElement>;
}) => (
  <tr ref={ref} className={styles.tableHeadRowStyle} {...restProps}>
    {children}
  </tr>
);
TableHeadRow.displayName = 'TableHeadRow';

export const TableHeadRowCell = ({
  ref,
  children,
  className,
  ...restProps
}: AllHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.Ref<HTMLTableCellElement>;
}) => (
  <th ref={ref} {...restProps} className={classNames(styles.tableHeadCellStyle, className)}>
    {children}
  </th>
);
TableHeadRowCell.displayName = 'TableHeadRowCell';

export const TableBody = ({
  ref,
  children,
  ...restProps
}: AllHTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => {
  return (
    <tbody ref={ref} {...restProps}>
      {children}
    </tbody>
  );
};
TableBody.displayName = 'TableBody';

interface TableBodyRowProps extends AllHTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
}

export const TableBodyRow = ({
  ref,
  children,
  className,
  selected,
  ...restProps
}: TableBodyRowProps & {
  ref?: React.Ref<HTMLTableRowElement>;
}) => (
  <tr ref={ref} {...restProps} className={classNames(styles.tableBodyRowStyle({ selected }), className)}>
    {children}
  </tr>
);
TableBodyRow.displayName = 'TableBodyRow';

interface TableBodyRowCellProps extends AllHTMLAttributes<HTMLTableCellElement> {
  selected?: boolean;
}

export const TableBodyRowCell = ({
  ref,
  children,
  className,
  selected,
  ...restProps
}: TableBodyRowCellProps & {
  ref?: React.Ref<HTMLTableCellElement>;
}) => (
  <td ref={ref} {...restProps} className={classNames(styles.tableBodyRowCellStyle({ selected }), className)}>
    {children}
  </td>
);
TableBodyRowCell.displayName = 'TableBodyRowCell';
